import React, { memo, useEffect, useMemo } from 'react';
import head from 'lodash/head';
import isFunction from 'lodash/isFunction';
import { ViewSizeGuide } from '@yme/assets/icons';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { colorCoreBlack900Base } from '@yme/tokens/colors';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { RegularLinkLevel1 } from '@yme/tokens/typography';
import { buildDynamicSrc, DigitalDataMutation, getImageUrlByDimension, setDigitalData, } from '@yme/utils/common';
import { DEFAULT_LANG, MOBILE_KEY } from '@yme/utils/constants';
import { ScriptLoader } from './ScriptLoader';
import { EXTERNAL_WIDGET_SCRIPT, IFRAME_OZ_GENERIC_CONTAINER, ONLINE_SIZING_BIKE_ID, OZ_BUTTON_SELECTOR, SIZE_GUIDE_BTN_DEFAULT_PROPS, SMARTFIT_STYLE_CONFIG, } from './constants';
import { LinkStyled } from './styled';
import { useInitOnlineSizingSettings } from './useInitOnlineSizingSettings';
/**
 * Component for rendering a frame size widget button with dynamic parameters.
 * @param {string} lang - The language code used for the widget.
 * @param {string} sku - The SKU code for the product displayed in the widget.
 * @param {Array} images - The array of images for the product displayed in the widget.
 * @param {string} productName - The name of the product displayed in the widget.
 * @param {string} smartfitKey - The API key used for initializing the Smartfit widget.
 * @param {Array} productVariants - The array of product variants displayed in the widget.
 * @param {number} stock - The quantity of product in stock displayed in the widget.
 * @param {number} price - The price of the product displayed in the widget.
 * @param {string} linkLabel - The label text for the widget button.
 * @param {string} slug - The product slug used for removing the iframe widget from the DOM.
 * @param {Function} onClickHandler - The function to be called on button click.
 * @returns {ReactElement|null} The rendered component or null if required props are missing.
 */
function FrameSizeWidgetComponent({ lang: currentLang = DEFAULT_LANG, sku = '', images = [], productName = '', smartfitKey, stock = 0, price, linkLabel, slug, onClickHandler, }) {
    useInitOnlineSizingSettings({
        apiKey: smartfitKey,
        lang: currentLang,
        generalFeatureId: ONLINE_SIZING_BIKE_ID,
        config: SMARTFIT_STYLE_CONFIG,
        onClickHandler,
    });
    const dynamicSrc = buildDynamicSrc(EXTERNAL_WIDGET_SCRIPT);
    const firstImageUrl = useMemo(() => {
        return head(images)?.url;
    }, [images]);
    // Dynamic params for widget
    // TODO: (D.K.) Need add placeholder for Image (if image is not available)
    const dynamicFrameSizeOptions = useMemo(() => {
        const imageUrl = getImageUrlByDimension({
            src: firstImageUrl || '',
            dimension: MOBILE_KEY,
        });
        return {
            type: 'button',
            'data-locale': currentLang,
            'data-oz-code': sku,
            'data-oz-name': productName,
            'data-oz-image': imageUrl,
            'data-price-value': price,
            // additional params for define availability of product in widget window
            'data-stock': stock,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLang, images, firstImageUrl, productName, sku]);
    useEffect(() => {
        const oz = window?.oz;
        const widgetBtnSelector = document.getElementById(OZ_BUTTON_SELECTOR);
        if (oz && widgetBtnSelector && isFunction(oz?.updateWidgetConfigurations)) {
            oz?.updateWidgetConfigurations([widgetBtnSelector]);
        }
    }, [slug, sku]);
    const removeIframeWidget = () => {
        const iframeWidget = document.getElementById(IFRAME_OZ_GENERIC_CONTAINER);
        if (iframeWidget && iframeWidget?.parentNode) {
            iframeWidget.parentNode.removeChild(iframeWidget);
        }
    };
    useEffect(() => {
        window.addEventListener('beforeunload', removeIframeWidget);
        return () => {
            window.removeEventListener('beforeunload', removeIframeWidget);
            removeIframeWidget();
        };
    }, [slug]);
    const onWidgetClick = () => {
        setDigitalData(DigitalDataMutation.SMART_FIT_START, {
            sku,
        });
    };
    if (!linkLabel || !sku || !slug) {
        return null;
    }
    return (React.createElement(FlexWrapper, null,
        React.createElement(SVGIcon, { type: ViewSizeGuide, size: ICON_SIZE.s }),
        React.createElement(LinkStyled
        // styled props
        , { 
            // styled props
            as: RegularLinkLevel1, weight: 400, color: colorCoreBlack900Base, "data-testid": "frame-size-widget", onClick: onWidgetClick, ...SIZE_GUIDE_BTN_DEFAULT_PROPS, ...dynamicFrameSizeOptions }, linkLabel),
        React.createElement(ScriptLoader, { url: dynamicSrc, id: ONLINE_SIZING_BIKE_ID })));
}
export const FrameSizeWidget = memo(FrameSizeWidgetComponent);
